/**
 * Asking for a reset link.
 *
 * The confirmation is deliberately vague — "if that address has an account, a link is on its
 * way". Saying "no such user" would turn this box into a free membership checker for anybody
 * curious about which agencies use the product.
 */

import { type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { Alert, Button, Card, Field, Input } from "@/components/ui";
import { AuthShell } from "./AuthShell";
import { usePageTitle } from "@/lib/title";

export function ForgotPasswordPage() {
  usePageTitle("Forgotten password");
  const ask = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.postAsHuman("/auth/forgot-password", input, "forgot_password"),
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    ask.mutate({ email: String(form.get("email") ?? "") });
  };

  return (
    <AuthShell>
      <Card>
        <h1 className="text-xl font-semibold">Forgotten your password?</h1>
        <p className="mt-1 text-sm text-ink-500">
          Type the address you sign in with and we will send you a link to set a new one.
        </p>

        {ask.isSuccess ? (
          <div className="mt-5 space-y-4">
            <Alert tone="success" title="Check your email.">
              If that address has an account, a reset link is on its way. It works once, and stops
              working after an hour.
            </Alert>
            <Link to="/sign-in" className="link text-sm">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-4">
            {ask.errorMessage ? <Alert tone="danger">{ask.errorMessage}</Alert> : null}

            <Field label="Email address" htmlFor="email" required>
              <Input id="email" name="email" type="email" autoComplete="email" required placeholder="you@agency.com" />
            </Field>

            <Button type="submit" disabled={ask.isPending} className="w-full">
              {ask.isPending ? "Sending…" : "Send the link"}
            </Button>

            <p className="text-sm text-ink-500">
              Remembered it? <Link to="/sign-in" className="link">Sign in</Link>
            </p>
          </form>
        )}
      </Card>
    </AuthShell>
  );
}
